// deps
import { useEffect, useMemo, useRef } from "react";
import { recordNREvent, getNRExtraFields } from "@checkout-core/newrelic";

// intenal-deps
import { useEffectOnce } from "@swiggy-private/core-ui";
import { addRequestInterceptor, addResponseInterceptor, NativeBridge } from "@swiggy-private/native-bridge";
import Blaze from "@swiggy-private/blaze.js";

// hooks
import { useKeyboardState } from "./UseKeyboardState";

// utils
import { GlobalHeaderState, SwgyHeaderType } from "@checkout-core/utils/Headers";
import { initBridgeSdk } from "@checkout-core/utils/BridgeSdk";
import { inputCaretMovementFixer } from "@checkout-core/utils/Dom";

// constants
import { NEWRELIC_TABLES } from "@checkout-core/constants/NewRelicTables";

const isIos = (): boolean =>
    typeof navigator !== "undefined" && (navigator.userAgent.match(/(iPad|iPhone|iPod)/g) ? true : false);

const createNBRequestInterceptor = (): VoidFunction =>
    addRequestInterceptor((req) => {
        recordNREvent({
            eventType: NEWRELIC_TABLES.PAYMENT_WEB_NATIVE_BRIDGE_REQUEST,
            id: req.requestId,
            type: req.type,
            subType: (req.payload as { state?: string })?.state ?? "",
        });
    });

const createNBResponseInterceptor = (): VoidFunction =>
    addResponseInterceptor((res) => {
        recordNREvent({
            eventType: NEWRELIC_TABLES.PAYMENT_WEB_NATIVE_BRIDGE_RESPONSE,
            id: res.requestId,
            type: res.type,
            statusCode: res.statusCode ?? -1,
            statusMessage: res.statusMessage ?? "",
            bridgeType: res.bridgeType ?? "",
            responseTime: (res as { timestamp?: number })?.timestamp ?? -1,
        });
    });

export function useInit(props: {
    headers: SwgyHeaderType;
    nbSdk?: NativeBridge;
    newrelicEndpoint?: string;
    newrelicKey?: string;
    environment?: string;
}): void {
    const initRef = useRef(false);
    if (!initRef.current) {
        GlobalHeaderState.init(props.headers);
        initBridgeSdk(props.nbSdk);
    }

    useEffect(() => {
        //when TurboPaymentWidget is used in IM flows the cartKey changes on cart changes and headers has the stale cartkey value from 1st mount.
        GlobalHeaderState.updateHeaders({ cartkey: props.headers?.cartkey });
    }, [props.headers?.cartkey]);

    const requestInterceptor = useMemo(() => createNBRequestInterceptor(), []);
    const responseInterceptor = useMemo(() => createNBResponseInterceptor(), []);

    useEffectOnce(() => {
        if (props.newrelicEndpoint && props.newrelicKey) {
            window.__NR_ENDPOINT__ = props.newrelicEndpoint;
            window.__NR_KEY__ = props.newrelicKey;
        }

        if (props.environment) {
            window.__ENVIRONMENT__ = props.environment;
        }
    });

    useEffectOnce(() => {
        Blaze.init({
            sendToAnalytics: (): void => {
                void 0;
            },
            enableElementTiming: false,
            enableResourceTiming: false,
            nrConfig: {
                enable: true,
                config: {
                    NR_ENDPOINT: window.__NR_ENDPOINT__ || "",
                    NR_KEY: window.__NR_KEY__ || "",
                },
                meta: {
                    appName: "checkout-web",
                    ...getNRExtraFields(),
                },
            },
        });
    });

    useEffectOnce(() => {
        initRef.current = true;

        if (isIos()) {
            const unlisten = inputCaretMovementFixer();
            return unlisten;
        }
    });

    useKeyboardState();
    useEffect(() => {
        return (): void => {
            requestInterceptor();
            responseInterceptor();
        };
    }, [requestInterceptor, responseInterceptor]);
}
